import { useState, useEffect, useCallback, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Youtube, ExternalLink, Copy, Check, Link2, Loader2, ShieldAlert, CheckCircle2, PlusCircle, Search,
} from "lucide-react";

const CREATE_URL = "https://m.youtube.com/create_channel?chromeless=1&next=/channel_creation_done";
const SWITCHER_URL = "https://www.youtube.com/channel_switcher";
const CRED_REF = "integrations:youtube";

function CopyField({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed — select and copy manually."); }
  };
  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wide text-slate-600">{label}</p>
        <p className="text-sm text-slate-200 truncate" data-testid={`${testid}-value`}>{value}</p>
      </div>
      <Button size="sm" variant="outline" onClick={copy} data-testid={testid}
        className="border-slate-700 text-slate-300 hover:bg-slate-800 h-8 flex-none">
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      </Button>
    </div>
  );
}

function CopyFieldButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed."); }
  };
  return (
    <Button size="sm" variant="outline" onClick={copy} data-testid={testid}
      className="border-slate-700 text-slate-300 hover:bg-slate-800 h-8 flex-none">
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
}

function ConnectionStatus({ conn, onDisconnect, onReconnect, busy }) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="border border-emerald-500/30 rounded-xl p-4 bg-emerald-500/5" data-testid="yt-connected-badge">
      <div className="flex items-center gap-2 text-emerald-300 text-sm font-medium">
        <CheckCircle2 className="w-4 h-4" /> YouTube connected
      </div>
      <p className="text-sm text-slate-200 mt-1 break-words">{conn.youtube_channel_title}</p>
      <p className="text-xs text-slate-500 font-mono break-all">{conn.youtube_channel_id}</p>
      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={() => setConfirming(true)} disabled={busy}
          className="h-7 border-rose-700/50 text-rose-300 hover:bg-rose-500/10" data-testid="yt-disconnect-btn">
          {busy === "disconnect" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Disconnect"}
        </Button>
        <Button size="sm" variant="outline" onClick={onReconnect} disabled={busy}
          className="h-7 border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="yt-reconnect-btn">
          Reconnect (choose another)
        </Button>
      </div>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="bg-[#111827] border-slate-700 text-slate-100" data-testid="yt-disconnect-dialog">
          <DialogHeader>
            <DialogTitle>Disconnect this YouTube channel?</DialogTitle>
            <DialogDescription className="text-slate-400">
              This unlinks <span className="text-slate-200">{conn.youtube_channel_title}</span> from this profile and revokes the Google grant (best-effort). Scheduled uploads for this channel will be cancelled. <b className="text-slate-300">Nothing on YouTube is deleted.</b> You can Reconnect a different channel afterwards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="yt-disconnect-cancel">Keep connected</Button>
            <Button onClick={() => { setConfirming(false); onDisconnect(); }} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="yt-disconnect-confirm">Disconnect</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChannelSelectorDialog({ open, onOpenChange, channels, selected, setSelected,
                                 setNeedConfirm, needConfirm, binding, doBind, chooseAnotherAccount, bindError }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border-slate-700 text-slate-100 max-w-lg" data-testid="yt-connect-dialog">
        <DialogHeader>
          <DialogTitle>Confirm the real YouTube channel</DialogTitle>
          <DialogDescription className="text-slate-400">
            Select the real YouTube channel to connect to this internal profile. We validate the identity via the YouTube API before binding — nothing is preselected, and a single result must still be chosen explicitly.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-sky-500/25 bg-sky-500/5 p-2.5 text-[11px] text-sky-200/90 leading-relaxed" data-testid="yt-sameemail-help">
          <b>Bir Google hesabında bir neçə kanal?</b> Google hansı kanalın icazə verildiyini öz ekranında soruşur. Aşağıda yalnız icazə verilmiş kanal(lar) görünür. Düzgün kanal deyilsə, <b>“Bu kanal deyil? Yenidən qoşul”</b> düyməsi ilə Google ekranında düzgün kanalı seç.
          <span className="block mt-1 text-slate-400">Multiple channels under one Google email? The choice happens on Google’s screen — only the authorized channel appears here. Use “Not this channel? Reconnect” to pick a different one on Google.</span>
        </div>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {channels.map((c) => (
            <button key={c.id} onClick={() => { setSelected(c); setNeedConfirm(false); }}
              data-testid={`yt-channel-option-${c.id}`}
              className={`w-full flex items-center gap-3 rounded-lg border p-2.5 text-left transition-colors ${selected?.id === c.id ? "border-violet-500 bg-violet-600/10" : "border-slate-800 hover:bg-slate-800/50"}`}>
              {c.thumbnail
                ? <img src={c.thumbnail} alt="" className="w-9 h-9 rounded-full flex-none" />
                : <span className="w-9 h-9 rounded-full bg-slate-700 flex-none flex items-center justify-center"><Youtube className="w-4 h-4 text-red-400" /></span>}
              <div className="min-w-0">
                <p className="text-sm text-slate-100 break-words">{c.title}</p>
                <p className="text-[11px] text-slate-500 font-mono break-all">{c.id}</p>
              </div>
              {selected?.id === c.id && <Check className="w-4 h-4 text-violet-300 ml-auto flex-none" />}
            </button>
          ))}
        </div>
        {bindError && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-2.5 text-xs text-rose-200 leading-relaxed break-words whitespace-normal" data-testid="yt-bind-error">
            {bindError}
          </div>
        )}
        <DialogFooter className="flex-col sm:flex-row flex-wrap gap-2">
          <Button variant="ghost" onClick={chooseAnotherAccount} className="text-slate-400 hover:bg-slate-800 sm:mr-auto text-left whitespace-normal break-words min-w-0 h-auto py-2" data-testid="yt-choose-another-account">Bu kanal deyil? Yenidən qoşul (Google-da seç)</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800 whitespace-normal break-words min-w-0 h-auto py-2" data-testid="yt-cancel-bind">Cancel</Button>
          {needConfirm ? (
            <Button onClick={() => doBind(true)} disabled={binding} className="bg-amber-600 hover:bg-amber-500 text-white whitespace-normal break-words min-w-0 h-auto py-2" data-testid="yt-confirm-bind-anyway">
              {binding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Proceed anyway"}
            </Button>
          ) : (
            <Button onClick={() => doBind(false)} disabled={binding || !selected} className="bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-40 whitespace-normal break-words min-w-0 h-auto py-2" data-testid="yt-confirm-bind">
              {binding ? <Loader2 className="w-4 h-4 animate-spin" /> : selected ? "Bind & connect" : "Select a channel first"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function YouTubeCreateConnect({ channelId, name, handle, description }) {
  const [conn, setConn] = useState(null);        // connected binding for this channel
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [channels, setChannels] = useState([]);
  const [selected, setSelected] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [binding, setBinding] = useState(false);
  const [bindError, setBindError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [needConfirm, setNeedConfirm] = useState(false); // 409 mismatch/duplicate → proceed anyway
  const [handleHint, setHandleHint] = useState(null);
  const [handleChecking, setHandleChecking] = useState(false);
  const [returned, setReturned] = useState(false);   // user came back from the create link
  const clickedCreate = useRef(false);

  useEffect(() => {
    const onBack = () => { if (clickedCreate.current && document.visibilityState === "visible") setReturned(true); };
    document.addEventListener("visibilitychange", onBack);
    window.addEventListener("focus", onBack);
    return () => { document.removeEventListener("visibilitychange", onBack); window.removeEventListener("focus", onBack); };
  }, []);

  const checkHandle = async () => {
    if (!handle) return;
    setHandleChecking(true); setHandleHint(null);
    try {
      const { data } = await api.get(`/youtube/handle-check?handle=${encodeURIComponent(handle.replace(/^@/, ""))}`);
      setHandleHint(data);
    } catch (e) { setHandleHint({ status: "unknown", hint: apiError(e), is_hint: true }); }
    finally { setHandleChecking(false); }
  };

  const loadConn = useCallback(async () => {
    if (!channelId) return;
    try {
      const { data } = await api.get(`/youtube/connections?app_channel_id=${channelId}`);
      setConn((data || []).find((c) => c.health === "connected") || null);
    } catch (e) { logger.error("Failed to load YouTube connection", e); /* passive load — no user action to surface against */ }
  }, [channelId]);
  useEffect(() => { loadConn(); }, [loadConn]);

  const startConnect = async () => {
    setLoading(true); setChecklist(null);
    try {
      const { data } = await api.get("/youtube/accessible-channels");
      if (!data.configured) {
        setChecklist(data.detail || "Google OAuth is not configured.");
        return;
      }
      if (!data.connected) {
        // Use the EXISTING OAuth flow — bind the INTENDED profile into the state.
        // FIX (Phase 25): ALWAYS force the Google account chooser (prompt=select_account)
        // so a fresh per-channel sign-in is possible — never silently reuse the session.
        try {
          const { data: au } = await api.get(`/youtube/auth-url?app_channel_id=${encodeURIComponent(channelId)}&prompt=select_account`);
          const win = window.open(au.url, "_blank", "noopener,noreferrer");
          if (!win) { setChecklist("Popup blocked — allow popups for this site, then try again."); return; }
          toast.message("Google OAuth opened", { description: "Choose the account, authorize, then click ‘Kanalı yaratdım — YouTube-a qoş’ again." });
        } catch (e) { setChecklist(apiError(e)); }
        return;
      }
      if ((data.channels || []).length === 0) {
        setChecklist("Authorized, but this Google account manages no channels. Create/switch to the right account, or use ‘Choose another account’.");
        return;
      }
      // FIX 1 — NOTHING is preselected, even for a single result. The owner must
      // explicitly select + confirm the real identity before we bind.
      setChannels(data.channels); setSelected(null); setNeedConfirm(false); setBindError(null); setPickerOpen(true);
    } catch (e) { setChecklist(apiError(e)); }
    finally { setLoading(false); }
  };

  const chooseAnotherAccount = async () => {
    try {
      const { data: au } = await api.get(`/youtube/auth-url?app_channel_id=${encodeURIComponent(channelId)}&prompt=select_account`);
      const win = window.open(au.url, "_blank", "noopener,noreferrer");
      if (!win) { toast.error("Popup blocked — allow popups, then try again."); return; }
      toast.message("Pick a different Google account", { description: "After authorizing, click connect again to choose the channel." });
      setPickerOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const doBind = async (proceedAnyway = false) => {
    if (!selected) return;
    setBinding(true); setBindError(null);
    try {
      await api.post("/youtube/connections/bind", {
        app_channel_id: channelId,
        youtube_channel_id: selected.id,
        youtube_channel_title: selected.title,
        credential_ref: CRED_REF,
        confirm_rebind: proceedAnyway,
        confirm_duplicate: proceedAnyway,
      });
      toast.success(`YouTube connected: ${selected.title}`);
      setPickerOpen(false); setNeedConfirm(false); setBindError(null);
      await loadConn();
    } catch (e) {
      const msg = apiError(e);
      setBindError(msg);
      if (e?.response?.status === 409) {
        setNeedConfirm(true);
      }
      toast.error(msg);
    } finally { setBinding(false); }
  };

  const disconnect = async (confirm = false) => {
    setBusy("disconnect");
    try {
      const { data } = await api.post(`/youtube/connections/${channelId}/disconnect`, { confirm });
      toast.success("YouTube disconnected", { description: data.note });
      setConn(null);
      await loadConn();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm(apiError(e) + "\n\nProceed and cancel those jobs?")) { await disconnect(true); return; }
      } else { toast.error(apiError(e)); }
    } finally { setBusy(null); }
  };

  if (conn) {
    return <ConnectionStatus conn={conn} busy={busy} onDisconnect={() => disconnect(false)} onReconnect={startConnect} />;
  }

  return (
    <div className="border border-slate-800 rounded-xl p-4 bg-slate-900/40 space-y-3" data-testid="yt-create-connect">
      <div className="flex items-center gap-2 text-slate-200 text-sm font-medium">
        <Youtube className="w-4 h-4 text-red-500" /> YouTube
      </div>

      {(name || handle || description) && (
        <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/60 p-3">
          <CopyField label="Channel name" value={name} testid="yt-copy-name" />
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wide text-slate-600">Suggested handle</p>
              <p className="text-sm text-slate-200 truncate" data-testid="yt-handle-value">{handle}</p>
              {handleHint && (
                <p className={`text-[11px] mt-0.5 ${handleHint.status === "available" ? "text-emerald-400" : handleHint.status === "taken" ? "text-amber-400" : "text-slate-500"}`} data-testid="yt-handle-hint">
                  {handleHint.hint}{handleHint.is_hint ? " (hint)" : ""}
                </p>
              )}
            </div>
            {handle && (
              <Button size="sm" variant="outline" onClick={checkHandle} disabled={handleChecking} data-testid="yt-check-handle"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 h-8 flex-none">
                {handleChecking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              </Button>
            )}
            <CopyFieldButton value={handle} testid="yt-copy-handle" />
          </div>
          <CopyField label="Description" value={description} testid="yt-copy-description" />
          <p className="text-[11px] text-amber-300/90 flex items-start gap-1 pt-0.5" data-testid="yt-copy-note">
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 flex-none" />
            Bu dəyərlər YouTube-da avtomatik doldurulmur və handle-ın əlçatanlığı zəmanətli deyil — əl ilə yapışdırın.
          </p>
        </div>
      )}

      <a href={CREATE_URL} target="_blank" rel="noopener noreferrer" data-testid="yt-create-channel-btn"
        onClick={() => { clickedCreate.current = true; }}
        className="inline-flex items-center justify-center gap-1.5 rounded-md bg-red-600 hover:bg-red-500 text-white text-sm font-medium px-4 py-2 transition-colors">
        <PlusCircle className="w-4 h-4" /> YouTube-da kanal yarat <ExternalLink className="w-3.5 h-3.5" />
      </a>
      <p className="text-xs text-slate-400" data-testid="yt-create-helper">
        YouTube-da kanalı yarat, sonra buraya qayıdıb qoş.
      </p>

      <div className="text-xs text-slate-500">
        <a href={SWITCHER_URL} target="_blank" rel="noopener noreferrer" data-testid="yt-extra-channel-link"
          className="text-violet-300 hover:text-violet-200 inline-flex items-center gap-1">
          Əlavə kanal yarat <ExternalLink className="w-3 h-3" />
        </a>
        <span className="ml-1">— artıq kanalınız varsa, orada “Create a new channel” seçin.</span>
      </div>

      <div className="pt-1 border-t border-slate-800">
        <Button onClick={startConnect} disabled={loading} data-testid="yt-connect-btn"
          className={`bg-violet-600 hover:bg-violet-500 text-white w-full sm:w-auto transition-shadow ${returned ? "ring-2 ring-violet-400 ring-offset-2 ring-offset-slate-900 animate-pulse" : ""}`}>
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Link2 className="w-4 h-4 mr-1.5" />}
          Kanalı yaratdım — YouTube-a qoş
        </Button>
        {checklist && (
          <div className="mt-2 text-xs text-amber-300 border border-amber-500/30 bg-amber-500/5 rounded-lg p-2.5" data-testid="yt-not-configured">
            {checklist} <a href="/integrations" className="underline text-violet-300">Integrations</a>
          </div>
        )}
      </div>

      <ChannelSelectorDialog
        open={pickerOpen} onOpenChange={setPickerOpen}
        channels={channels} selected={selected} setSelected={setSelected}
        setNeedConfirm={setNeedConfirm} needConfirm={needConfirm}
        binding={binding} doBind={doBind} chooseAnotherAccount={chooseAnotherAccount} bindError={bindError}
      />
    </div>
  );
}
